import { BaseEdgeFunctionClient } from '../shared/base-client';
import { 
  ResearchRequest, 
  ResearchResponse,
  ResearchParameters,
  PerplexityConfig 
} from './types';

export class PerplexityResearchClient extends BaseEdgeFunctionClient {
  constructor(config: PerplexityConfig = {}) {
    super(config);
  }

  /**
   * Generate research using Perplexity
   */
  async generateResearch(request: ResearchRequest): Promise<ResearchResponse> {
    try {
      const response = await this.invokeEdgeFunction<ResearchResponse>(
        'research-perplexity',
        {
          ...request,
          appName: request.appName || 'Research Library',
          functionName: request.functionName || 'Research Generation'
        }
      );

      return response;
    } catch (error) {
      this.handleError(error, 'Research generation failed');
    }
  }

  /**
   * Generate research with specific type and filters
   */
  async generateTypedResearch(
    prompt: string,
    researchType: string,
    options: {
      timeframe?: string;
      country?: string;
      sourceType?: string;
      filters?: Record<string, string>;
    } = {}
  ): Promise<ResearchResponse> {
    const request: ResearchRequest = {
      researchPrompt: prompt,
      researchType,
      timeframe: options.timeframe,
      country: options.country,
      sourceType: options.sourceType,
      selectedFilters: options.filters
    };

    return this.generateResearch(request);
  }

  /**
   * Generate general research without specific type
   */
  async generateGeneralResearch(
    prompt: string,
    options: {
      timeframe?: string;
      country?: string;
      sourceType?: string;
    } = {}
  ): Promise<ResearchResponse> {
    return this.generateTypedResearch(prompt, 'General Research', options);
  }

  /**
   * Generate research for specific domains
   */
  async generateDomainResearch(
    prompt: string,
    domain: 'Technology & Innovation' | 'Business & Professional' | 'Content & Creative' | 'Personal Development' | 'Academic & Learning' | 'Current Events & News',
    options: {
      timeframe?: string;
      country?: string;
      sourceType?: string;
      filters?: Record<string, string>;
    } = {}
  ): Promise<ResearchResponse> {
    return this.generateTypedResearch(prompt, domain, options);
  }

  /**
   * Generate research with related questions
   */
  async generateResearchWithQuestions(request: ResearchRequest): Promise<{
    research: ResearchResponse;
    relatedQuestions: string[];
  }> {
    const research = await this.generateResearch(request);
    
    return {
      research,
      relatedQuestions: research.related_questions || []
    };
  }

  /**
   * Build research parameters object for database storage
   */
  buildResearchParameters(
    request: ResearchRequest,
    response: ResearchResponse
  ): ResearchParameters {
    return {
      research_type: request.researchType || 'General Research',
      timeframe: request.timeframe || null,
      timeframe_label: response.timeframeLabel || null,
      date_range_start: response.dateRangeStart || null,
      date_range_end: response.dateRangeEnd || null,
      country: request.country || null,
      source_type: request.sourceType || null,
      type_specific_filters: request.selectedFilters || {}
    };
  }
} 